import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { getFellowMentor } from '@/constants/fellowsCorner';
import type { ExpertProfile } from '@/util/mapDirectoryProfiles';

type MentorSelectCardProps = {
  expert: ExpertProfile;
  isSelected: boolean;
  onSelect: () => void;
};

/**
 * Selectable mentor tile in the "Meet your mentors" lineup: a left radio, then
 * avatar, name with a specialty badge, position, and institution — mirroring the
 * expert cards in the `/inquiry` expert-selection sheet (ExpertMultiSelect's
 * ExpertItem, whose select control sits on the left). Picking one reveals the ask
 * form.
 *
 * Selection is conveyed by the recolored border *and* the filled radio, not color
 * alone, so it stays legible without relying on hue (see accessibility rules).
 * It also has to reach assistive tech, which took both props. ARIA defines
 * `aria-checked` for a radio, so the state key is `checked` rather than
 * `selected`, and the attribute is set directly because this React Native Web
 * version emits no `aria-*` from `accessibilityState` alone. Verified against
 * the rendered DOM: before this, the group announced no selection at all.
 * `CheckboxCard` pairs them the same way.
 * The border weight is constant across states so selecting only recolors and
 * never shifts layout.
 *
 * Sizes to its own content, with no `h-full`. The lineup is an auto-height flex
 * column, so a percentage height on a card has no definite parent to resolve
 * against: every card inflated to one shared height, the column collapsed to a
 * single card, and the overflowing cards painted over the ask form below, which
 * then swallowed their clicks. That read as a mentor you could see but could not
 * select. Equalize heights only under a layout that gives the row a definite one.
 *
 * Deliberately carries no away marker. These mentors are not substitutes for one
 * another, so availability does not change who a fellow should pick, only what
 * they should expect, and no chip-length label can say "away, but send it
 * anyway and they will reply". The ask form states it in full once a mentor is
 * chosen. Revisit if the roster ever grows into interchangeable mentors, where
 * availability would genuinely inform the choice.
 */
export function MentorSelectCard({ expert, isSelected, onSelect }: MentorSelectCardProps) {
  const registryMentor = getFellowMentor(expert.id);

  const specialty = expert.speciality;

  const position = expert.title ?? registryMentor?.title;

  return (
    <Pressable
      onPress={onSelect}
      accessibilityRole="radio"
      accessibilityState={{ checked: isSelected }}
      aria-checked={isSelected}
      className={twJoin(
        'flex-row items-center gap-3 rounded-xl border p-3 web:cursor-pointer web:transition-colors',
        isSelected
          ? 'bg-primary-50/60 border-primary-500'
          : 'border-outline-200 bg-background-0 web:hover:bg-background-50'
      )}>
      <View
        className={twJoin(
          'h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
          isSelected ? 'border-primary-500' : 'border-outline-400'
        )}>
        {isSelected ? <View className="h-2.5 w-2.5 rounded-full bg-primary-500" /> : null}
      </View>

      <Avatar size="md" className="shrink-0">
        <AvatarImage
          source={expert.avatarSrc ? { uri: expert.avatarSrc } : primum_default_avatar}
        />
      </Avatar>

      <VStack className="min-w-0 flex-1 gap-0.5">
        <HStack className="flex-wrap items-center gap-2">
          <Text size="sm" className="font-medium text-typography-800">
            {expert.name}
          </Text>
          {specialty ? (
            <Badge size="sm" action="info" className="self-start px-2">
              <BadgeText className="text-xs text-info-800">{specialty}</BadgeText>
            </Badge>
          ) : null}
        </HStack>
        {position ? (
          <Text size="xs" className="text-typography-700">
            {position}
          </Text>
        ) : null}
        {expert.institution ? (
          <Text size="xs" className="text-typography-700">
            {expert.institution}
          </Text>
        ) : null}
      </VStack>
    </Pressable>
  );
}

/**
 * Placeholder for the lineup while the roster loads, shaped like the card so the
 * list does not resize under the reader when the real one arrives. A spinner
 * gave no indication of how much was coming and collapsed to nothing when it
 * did.
 *
 * The count is a guess at a small curated roster rather than a promise. Being
 * one row out costs a short settle; being shapeless costs the whole layout.
 */
export function MentorSelectCardSkeleton({ count = 3 }: { count?: number }) {
  return (
    <VStack space="sm">
      {Array.from({ length: count }, (_, index) => (
        <HStack
          key={index}
          className="items-center gap-3 rounded-xl border border-outline-200 bg-background-0 p-3">
          <Skeleton variant="circular" className="h-5 w-5 shrink-0" />
          <Skeleton variant="circular" className="h-12 w-12 shrink-0" />
          <VStack className="min-w-0 flex-1 gap-1.5">
            <Skeleton variant="rounded" className="h-4 w-2/5" />
            <SkeletonText _lines={2} gap={1} className="h-2.5 w-3/5" />
          </VStack>
        </HStack>
      ))}
    </VStack>
  );
}
